import { Divider, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { MATRICE, TABLE } from '../../constants';
import {
  BlockProps,
  ChapterProps,
  InterrelatedFieldProps,
  QuestionAnswer,
  SectionProps,
  TabularAnswer,
} from '../../models/global';
import ToastMessage from '../ToastMessage/ToastMessage';
import UserField from './UserField';

interface UserFieldBlockProps {
  block: BlockProps;
  section: SectionProps;
  chapter: ChapterProps;
  chapters: ChapterProps[];
  index: number;
  language: string;
  questionAnswers: QuestionAnswer[];
  allQuestionAnswers: QuestionAnswer[];
  tabularAnswers: TabularAnswer[];
  setChapters: (chapters) => void;
  setTabularAnswers: (tabularAnswers) => void;
  readonly?: boolean;
  changeQuestionAnswer: (
    value: string,
    blockFieldType: string,
    blockId: string,
    sectionId: string,
    optionId?: string,
    interrelatedFields?: InterrelatedFieldProps[],
  ) => void;
  changeTabularAnswer: (
    value: string | boolean,
    columnType: string,
    sectionId: string,
    columnId: string,
    rowId: string,
    rowKey: string,
    matrixType?: string,
  ) => void;
  showLastSurvey?: boolean;
  isAdmin?: boolean;
  lastSurveys: { id: number; name: string }[];
}

function UserFieldBlock({
  block,
  section,
  chapter,
  index,
  language,
  chapters,
  questionAnswers,
  allQuestionAnswers,
  changeQuestionAnswer,
  changeTabularAnswer,
  setChapters,
  tabularAnswers,
  setTabularAnswers,
  readonly,
  showLastSurvey,
  isAdmin,
  lastSurveys,
}: UserFieldBlockProps) {
  const [toast, setToast] = useState('');

  const getRelatedField = (optionIds: string[] = [], section: SectionProps) => {
    const dependentQuestionIndex = section.fieldBlocks.findIndex(fieldBlock => {
      const fieldBlockOptions = fieldBlock.options?.map(el => el.id) || [];
      return optionIds.some(optionId => fieldBlockOptions.includes(optionId));
    });
    if (dependentQuestionIndex === -1) {
      return;
    }
    return {
      dependentQuestion: section.fieldBlocks[dependentQuestionIndex],
      dependentQuestionIndex,
    };
  };

  const answerIds = block.dependsOnOptions || block.interrelatedFields?.map(el => el.optionId);

  const relatedField = getRelatedField(answerIds, section);

  const showQuestion = relatedField
    ? !!allQuestionAnswers.find(questionAnswer => answerIds.includes(questionAnswer.optionId))
    : true;

  if (!showQuestion) {
    block.hide = true;
  } else {
    block.hide = false;
  }

  return (
    showQuestion && (
      <Box key={block.id} id={block.id} ml={1}>
        {![TABLE, MATRICE].includes(block.fieldType) && (
          <Box key={index} className='flex-center' style={language !== 'English' ? { alignItems: 'flex-start' } : {}}>
            <Box>
              <Typography variant='body2'>
                {chapter.order}.{section.order}.{block.order}{' '}
                {language === 'English' || !block[`question${language}`]
                  ? block.question
                  : `${block[`question${language}`]}`}
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ mb: 2.5 }}>
          <UserField
            chapterId={chapter.id}
            sectionId={section.id}
            block={block}
            language={language}
            changeQuestionAnswer={changeQuestionAnswer}
            questionAnswers={questionAnswers}
            changeTabularAnswer={changeTabularAnswer}
            setChapters={setChapters}
            chapters={chapters}
            tabularAnswers={tabularAnswers}
            setTabularAnswers={setTabularAnswers}
            readonly={readonly}
            setToast={setToast}
            showLastSurvey={showLastSurvey}
            isAdmin={isAdmin}
            lastSurvey={lastSurveys?.find(
              item =>
                item.id ===
                block?.[
                  block.fieldType === TABLE
                    ? 'referenceTable'
                    : block.fieldType === MATRICE
                      ? 'referenceMatrice'
                      : 'reference'
                ]?.surveyId,
            )}
          />
          {![TABLE, MATRICE].includes(block.fieldType) && <Divider sx={{ mt: 4 }} />}
        </Box>
        <ToastMessage toast={toast} close={() => setToast('')} messageType='success' autoHideDuration={1000} />
      </Box>
    )
  );
}

export default UserFieldBlock;
